import { formatDuration, type DayStat } from '@bb/shared/core';

/** Показатели статистики: как считать, как подписывать, каким цветом рисовать. */
export type MetricKey = 'sleep' | 'feed' | 'bottle' | 'diaper' | 'walk';

export interface Metric {
  key: MetricKey;
  label: string;
  /** Подпись для графика «… по дням». */
  title: string;
  /** Категория цвета (CSS-переменные --c-*, --*-bg). */
  cat: 'sleep' | 'feed' | 'diaper' | 'walk';
  value(d: DayStat): number;
  /** Полное значение: «13 ч 20 мин», «7,4», «320 мл». */
  format(v: number): string;
  /** Короткое — для подписей оси. */
  short(v: number): string;
  /** Единица для средних в счётных показателях. */
  unit?: string;
  /** Изменение: «+20 мин», «−0,5». */
  delta(v: number): string;
}

const num = (v: number, digits = 1) => (Math.round(v * 10 ** digits) / 10 ** digits).toLocaleString('ru-RU');
const sign = (v: number) => (v > 0 ? '+' : v < 0 ? '−' : '±');
const hours = (v: number) => {
  if (v < 60) return `${Math.round(v)}м`;
  const h = v / 60;
  return `${Number.isInteger(h) ? h : num(h)}ч`;
};

export const METRICS: Record<MetricKey, Metric> = {
  sleep: {
    key: 'sleep', label: 'Сон', title: 'Сон', cat: 'sleep',
    value: (d) => d.sleepMin, format: (v) => formatDuration(v), short: hours,
    delta: (v) => `${sign(v)}${formatDuration(Math.abs(v))}`,
  },
  feed: {
    key: 'feed', label: 'Кормления', title: 'Кормления', cat: 'feed', unit: 'раз',
    value: (d) => d.feedCount, format: (v) => num(v), short: (v) => num(v, 0),
    delta: (v) => `${sign(v)}${num(Math.abs(v))}`,
  },
  bottle: {
    key: 'bottle', label: 'Бутылочка', title: 'Бутылочка, мл', cat: 'feed',
    value: (d) => d.bottleMl, format: (v) => `${Math.round(v)} мл`, short: (v) => num(v, 0),
    delta: (v) => `${sign(v)}${Math.round(Math.abs(v))} мл`,
  },
  diaper: {
    key: 'diaper', label: 'Подгузники', title: 'Подгузники', cat: 'diaper', unit: 'шт',
    value: (d) => d.diaperCount, format: (v) => num(v), short: (v) => num(v, 0),
    delta: (v) => `${sign(v)}${num(Math.abs(v))}`,
  },
  walk: {
    key: 'walk', label: 'Прогулки', title: 'Прогулки', cat: 'walk',
    value: (d) => d.walkMin, format: (v) => formatDuration(v), short: hours,
    delta: (v) => `${sign(v)}${formatDuration(Math.abs(v))}`,
  },
};

/** Среднее за сутки по дням, где есть записи. */
export function avgOf(days: readonly DayStat[], f: (d: DayStat) => number): number | null {
  const withData = days.filter((d) => d.hasData);
  if (!withData.length) return null;
  return withData.reduce((s, d) => s + f(d), 0) / withData.length;
}

export function sumOf(days: readonly DayStat[], f: (d: DayStat) => number): number {
  return days.reduce((s, d) => s + f(d), 0);
}
